import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useNavigate, useParams } from "react-router-dom";
import { classNames } from "primereact/utils";
import OperationApi from "../../services/OperationApi";
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const EditFormOperation: React.FC = () => {
  const { t: translate } = useTranslation("form");
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  interface Unidade {
    id: string;
    name: string;
  }
  interface Area {
    id: string;
    name: string;
  }
  interface Linha {
    id: string;
    name: string;
  }
  interface Sublinha {
    id: string;
    name: string;
  }
  interface Type {
    id: string;
    name: string;
  }
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [sublinhas, setSublinhas] = useState<Sublinha[]>([]);
  const [tipos, setTipos] = useState<any[]>([]);

  const [unidade, setUnidade] = useState<Unidade | null>(null);
  const [area, setArea] = useState<Area | null>(null);
  const [linha, setLinha] = useState<Linha | null>(null);
  const [sublinha, setSublinha] = useState<Sublinha | null>(null);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [rank, setRank] = useState("");
  const [tipo, setTipo] = useState<Type | null>(null);
  const [status, setStatus] = useState<"1" | "0" | null>(null);
  const [errors, setErrors] = useState({ nome: "", descricao: "", rank: "" });
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const { t: translateMessage } = useTranslation("messages");

  const validate: any = () => {
    const newErrors = { nome: "", descricao: "", rank: "" };
    if (nome.length > 50) {
      newErrors.nome = translate("NameExceptionCharacters");
    }
    if (descricao.length > 500) {
      newErrors.descricao = translate("DescriptionExceptionCharacters");
    }
    if (rank.length > 3) {
      newErrors.rank = translate("RankExceptionCharacters");
    }
    setErrors(newErrors);
    return !newErrors.nome && !newErrors.descricao && !newErrors.rank;
  };

  const statusOptions = [
    { label: "Ativa", value: "1" },
    { label: "Inativa", value: "0" },
  ];

  useEffect(() => {
    const fetchInitialData = async (): Promise<void> => {
      if (!id) return;
      try {
        const opRes = await OperationApi.GetOperationDetailsById(id);
        // load unidades and selection chain
        const sub = opRes.data.subLine;
        const lin = sub.line;
        const ar = lin.area;
        const uni = ar.productionUnit;
        setUnidades([uni]);
        setUnidade(uni);
        setAreas([ar]);
        setArea(ar);
        setLinhas([lin]);
        setLinha(lin);
        setSublinhas([sub]);
        setSublinha(sub);
        setNome(opRes.data.name);
        setDescricao(opRes.data.description);
        setRank(opRes.data.rank.toString());

        // setTipos(await EditFormApi.GetAllOperationTypes().then((r) => r.data));
        // setTipo(opRes.data.operationTypeId);

        // Carrega todos os tipos
        const tiposData = await OperationApi.GetAllOperationTypes().then(
          (r) => r.data,
        );
        setTipos(tiposData);
        const tipoEncontrado = tiposData.find(
          (t: Type) => t.id === opRes.data.operationTypeId,
        );
        setTipo(tipoEncontrado || null);

        setStatus(opRes.data.isActive ? "1" : "0");
      } catch (error: any) {
        displayToast(
          "error",
          translateMessage("error"),
          translateMessage("operationNotFound"),
          false,
          3000,
        );
        setTimeout(() => {
          navigate("/operations");
        }, 2000); // espera 2 segundos para o usuário ver o toast
      }
    };
    fetchInitialData();
  }, [id]);

  const handleSave: any = async () => {
    let missing: string[] = [];
    if (!unidade) missing.push(translate("unit"));
    if (!area) missing.push(translate("area"));
    if (!linha) missing.push(translate("line"));
    if (!sublinha) missing.push(translate("subline"));
    if (!nome) missing.push(translate("name"));
    if (!descricao) missing.push(translate("description"));
    if (!rank) missing.push(translate("operationRank"));
    if (!tipo) missing.push(translate("Type"));
    if (!status) missing.push(translate("status"));
    if (missing.length) {
      displayToast(
        "error",
        "Erro",
        `${translate("requiredFieldsMissing")} : ${missing.join(", ")}`,
        false,
        3000,
      );
      return;
    }
    try {
      if (validate()) {
        await OperationApi.Update({
          id: id || "",
          name: nome,
          description: descricao,
          isactive: status === "1",
          rank: Number(rank),
          subLineId: sublinha!.id,
          operationTypeId: tipo!.id,
        });
        displayToast(
          "success",
          "Sucesso",
          translate("operationUpdatedSuccess"),
          false,
          3000,
        );
        setTimeout(() => navigate("/operations"), 1000);
      }
    } catch (error: any) {
      const backendMessage =
        error?.data?.data ||
        error?.data?.data?.[0] ||
        "Erro ao editar operação.";

      if (error.response?.status == 400 || backendMessage) {
        displayToast("error", "Erro", backendMessage, false, 3000);
      } else {
        displayToast("error", "Erro", backendMessage, false, 3000);
      }
    }
  };

  return (
    <FormCard
      title={translate("OperationEdit")}
      icon="edit"
      infoIcon="info"
      infoText="Altere os dados da operação."
      submitButtonLabel={translate("editOperation")}
      cancelButtonLabel={translate("Cancel")}
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
      onCancel={() => setShowConfirmCancel(true)}
      numColumns={6}
    >
      {/* Unidade */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("productionUnit")}
          required={Boolean(unidade)}
          value={unidade}
          options={unidades}
          optionLabel="name"
          placeholder={translate("selectUnit")}
          disabled={true}
          className="w-full"
          onChange={(val: any) => setUnidade(val)}
        />
      </div>

      {/* Área */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("area")}
          required={Boolean(area)}
          value={area}
          options={areas}
          optionLabel="name"
          placeholder={translate("selectArea")}
          disabled={true}
          className="w-full"
          onChange={(val: any) => setArea(val)}
        />
      </div>

      {/* Linha */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("line")}
          required={Boolean(linha)}
          value={linha}
          options={linhas}
          optionLabel="name"
          placeholder={translate("selectLine")}
          disabled={true}
          className="w-full"
          onChange={(val: any) => setLinha(val)}
        />
      </div>

      {/* Sublinha */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("subline")}
          required={Boolean(sublinha)}
          value={sublinha}
          options={sublinhas}
          optionLabel="name"
          placeholder={translate("selectSubline")}
          disabled={true}
          className="w-full"
          onChange={(val: any) => setSublinha(val)}
        />
      </div>

      {/* Nome */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("operationName")}
          required={Boolean(nome)}
          value={nome}
          onChange={(val: any) => setNome(val)}
          placeholder={`${translate("operationName")} *`}
          className={classNames("w-full flex", { "p-invalid border-red": errors.nome })}
        />
        {errors.nome && <small className="p-error">{errors.nome}</small>}
      </div>

      {/* Descrição */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="textArea"
          label={translate("operationDescription")}
          required={true}
          value={descricao}
          onChange={(val: any) => setDescricao(val)}
          placeholder={`${translate("operationDescription")} *`}
          rows={3}
          maxLength={600}
          className={classNames("w-full", { "p-invalid border-red": errors.descricao })}
        />
        {errors.descricao && <small className="p-error mb-4">{errors.descricao}</small>}
      </div>

      {/* Rank */}
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="text"
          label={translate("operationRank")}
          required={Boolean(rank)}
          value={rank}
          onChange={(val: any) => setRank(val)}
          placeholder={`${translate("operationRank")} *`}
          className={classNames("w-full", { "p-invalid": errors.rank })}
        />
        {errors.rank && <small className="p-error">{errors.rank}</small>}
      </div>

      {/* Tipo */}
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translate("Type")}
          required={Boolean(tipo)}
          value={tipo}
          options={tipos}
          optionLabel="name"
          placeholder={`${translate("Type")} *`}
          className="w-full"
          onChange={(val: any) => setTipo(val)}
        />
      </div>

      {/* Status */}
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translate("status")}
          required={Boolean(status)}
          value={status}
          options={statusOptions}
          placeholder={translate("status")}
          className="w-full"
          onChange={(val: any) => setStatus(val)}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/operations")}
      />
    </FormCard>
  );
};

export default EditFormOperation;
